import { cn } from '@/shared/lib/utils'
import { Image } from '@/shared/components/ui/image'

export interface ProductGridCardProps {
  /** Primary label (e.g. product short name). */
  title: string
  /** Secondary label under the title (e.g. category or template name). */
  subtitle?: string | null
  /** Full-resolution image URL; falls back to a Package placeholder when absent. */
  imageUrl?: string | null
  /** Low-resolution thumbnail painted (blurred) while the full image loads. */
  thumbnailUrl?: string | null
  imageAlt?: string
  /**
   * When provided, renders a status dot before the title (green for
   * ``PUBLISHED``, muted otherwise). Omit to hide the dot - e.g. the consumer
   * catalogue only ever shows published products, so the dot adds no signal.
   */
  status?: string
  /** Optional trailing label, e.g. a variant count. Hidden when absent. */
  badge?: string | null
  onClick?: () => void
  testId?: string
  className?: string
}

/**
 * Shared product tile used by the admin product library grid and the consumer
 * registration product browser. Keep this the single source of truth for the
 * grid card so both surfaces stay visually identical.
 */
export function ProductGridCard({
  title,
  subtitle,
  imageUrl,
  thumbnailUrl,
  imageAlt,
  status,
  badge,
  onClick,
  testId,
  className,
}: ProductGridCardProps) {
  return (
    <div
      data-testid={testId}
      role="button"
      tabIndex={0}
      className={cn(
        'cursor-pointer overflow-hidden rounded-[8px] border-[0.5px] border-solid border-[var(--rule)] transition-all duration-150 hover:border-[var(--rule-strong)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--green)]',
        className,
      )}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }}
    >
      {/* Image */}
      <Image
        src={imageUrl}
        thumbnailSrc={thumbnailUrl}
        alt={imageAlt ?? title}
        className="h-[104px] w-full bg-[var(--bg-subtle)]"
      />
      {/* Info */}
      <div className="p-3">
        <div className="mb-1 flex items-center gap-1.5">
          {status !== undefined && (
            <span
              className={cn(
                'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                status === 'PUBLISHED' ? 'bg-[var(--green)]' : 'bg-[var(--ink-3)]',
              )}
            />
          )}
          <p className="truncate text-[11px] text-[var(--ink)]">{title}</p>
        </div>
        {(subtitle || badge) && (
          <div className="flex items-center justify-between gap-2">
            {subtitle && (
              <p className="truncate text-[9px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                {subtitle}
              </p>
            )}
            {badge && (
              <span className="shrink-0 text-[9px] tracking-[0.06em] text-[var(--ink-3)]">
                {badge}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
